import { inRange, isNil } from 'lodash-es';
import { z } from 'zod';
import {
  ALREADY_USED_BRAND,
  MISSING_DIAMETER,
  OVERLAP_DIAMETER,
  PP_CUSTOM_CONSTRAINT,
  PRICING_TYPE_ENUM,
  diameterSchema,
  validateRanges,
} from '~/purchase/schemas';

import {
  MAX_LENGTH_MESSAGE,
  REQUIRED_MESSAGE,
  requiredNumber,
  requiredString,
} from '@wyz/utils';
import { IDiameter } from '~/purchase/types';
import {
  associatedCriteriaFormSchema,
  COMPUTED_PRICING_POLICY_STATUS,
} from '~/common/schemas';

export const redistributionPricingConfigurationStepFormSchema = z.object({
  name: requiredString.max(
    PP_CUSTOM_CONSTRAINT.NAME_MAX_LENGTH,
    MAX_LENGTH_MESSAGE,
  ),

  vehicleTypeGroup: requiredString,
  configurations: z
    .array(
      z
        .object({
          name: requiredString.max(
            PP_CUSTOM_CONSTRAINT.BLOC_ITEM_NAME_MAX_LENGTH,
            MAX_LENGTH_MESSAGE,
          ),
          brands: z.array(requiredNumber).min(1, REQUIRED_MESSAGE),
          supplierTypes: z.array(requiredString).min(1, REQUIRED_MESSAGE),
          refinementFlags: z.object({
            season: z.boolean(),
            tyreType: z.boolean(),
            diameter: z.boolean(),
            supplierType: z.boolean(),
          }),
          configurationType: z.enum(PRICING_TYPE_ENUM),
          configurationTypeValue: z.number().nullish(),
          definition: z
            .object({
              supplierTypes: z.record(
                z.string(),
                z.object({
                  refinements: z.array(
                    z.object({
                      diameter: diameterSchema,
                      seasons: z.record(
                        z.string(),
                        z.object({
                          tyreTypes: z.record(
                            z.string(),
                            z.object({
                              value: requiredNumber,
                            }),
                          ),
                        }),
                      ),
                    }),
                  ),
                }),
              ),
            })
            .nullish(),
        })
        .superRefine((formData, ctx) => {
          // Conditional validation
          const hasRefinement =
            formData.refinementFlags.diameter ||
            formData.refinementFlags.season ||
            formData.refinementFlags.supplierType ||
            formData.refinementFlags.tyreType;

          if (!hasRefinement) {
            if (isNil(formData.configurationTypeValue)) {
              ctx.addIssue({
                code: 'custom',
                message: REQUIRED_MESSAGE,
                path: ['configurationTypeValue'],
                fatal: true,
              });
            }
            const shouldBePercent =
              formData.configurationType === PRICING_TYPE_ENUM.DISCOUNT_BF ||
              formData.configurationType === PRICING_TYPE_ENUM.MARGIN;
            if (
              shouldBePercent &&
              !isNil(formData.configurationTypeValue) &&
              !inRange(formData.configurationTypeValue, 0, 101)
            ) {
              ctx.addIssue({
                code: 'custom',
                message: 'app.common.validations.percent_field',
                path: ['configurationTypeValue'],
                fatal: true,
              });
            }
          } else {
            if (!formData.definition) {
              ctx.addIssue({
                code: 'custom',
                message: REQUIRED_MESSAGE,
                path: ['definition'],
                fatal: true,
              });
            }
            // custom validation for diameter
            if (formData.refinementFlags.diameter) {
              Object.keys(formData.definition?.supplierTypes || {}).forEach(
                (supplierTypeCodeName) => {
                  const refinements =
                    formData.definition?.supplierTypes[supplierTypeCodeName]
                      .refinements || [];
                  const diameters: Array<IDiameter> = refinements
                    .filter((refinement) => refinement.diameter)
                    .map((refinement) => refinement.diameter as IDiameter);
                  const diameterValidation = validateRanges(diameters);
                  if (diameters.length > 0) {
                    if (diameterValidation.missing) {
                      ctx.addIssue({
                        code: 'custom',
                        message: MISSING_DIAMETER,
                        path: [
                          'definition',
                          'supplierTypes',
                          supplierTypeCodeName,
                          'refinements',
                        ],
                        fatal: true,
                      });
                    }
                    if (diameterValidation.overlap) {
                      ctx.addIssue({
                        code: 'custom',
                        message: OVERLAP_DIAMETER,
                        path: [
                          'definition',
                          'supplierTypes',
                          supplierTypeCodeName,
                          'refinements',
                        ],
                        fatal: true,
                      });
                    }
                  }
                },
              );
            }
          }
        }),
    )
    .min(1, REQUIRED_MESSAGE)
    .superRefine((configurations, ctx) => {
      const brandsBySource: Record<string, Record<string, boolean>> = {};
      configurations.forEach((configuration, configurationIdx) => {
        configuration.brands.forEach((brand) => {
          configuration.supplierTypes.forEach((supplierType) => {
            if (!brandsBySource[supplierType]) {
              brandsBySource[supplierType] = {};
            }
            if (brandsBySource[supplierType][brand]) {
              ctx.addIssue({
                code: 'custom',
                message: ALREADY_USED_BRAND,
                path: [configurationIdx, 'brands'],
                fatal: true,
              });
            } else {
              brandsBySource[supplierType][brand] = true;
            }
          });
        });
      });
    }),
});
export const redistributionPricingApplicationStepFormSchema = z.object({
  startDate: z.date({
    error: (issue) =>
      issue.input === undefined ? REQUIRED_MESSAGE : undefined,
  }),
  endDate: z.date().nullish(),
  supplierIds: z.array(z.string()).min(1, REQUIRED_MESSAGE),
  customersCriteria: z
    .array(associatedCriteriaFormSchema)
    .min(1, REQUIRED_MESSAGE),
});

export const redistributionPricingPolicySchema = z.object({
  id: z.string(),
  name: z.string(),
  createdBy: z.string(),
  updatedBy: z.string().nullable(),
  updatedAt: z.date().nullable(),
  brands: z.array(z.object({ name: z.string() })),
  formattedStartDate: z.string(),
  formattedEndDate: z.string().nullable(),
  computedStatus: z.enum(COMPUTED_PRICING_POLICY_STATUS),
  createdAt: z.date().nullable(),
  policyGroupId: z.number(),
  customerCriteria: z.array(
    z.object({
      id: z.string(),
      criterion: z.string(),
      code: z.string(),
      value: z.string(),
    }),
  ),
  suppliers: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
    }),
  ),
});

export const REDISTRIBUTION_PRICING_POLICY_FORM_STEPS = [
  'APPLICATION',
  'PRICING',
] as const;
